import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, useSEO } from "../../lib/api";
import { useAuth, googleLogin } from "../../context/AuthContext";

const inp = "w-full border border-[#E8E0D5] rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-[#6B8E78]";

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google-session", { session_id: sid }).then(({ data }) => {
      setUser(data);
      window.history.replaceState(null, "", location.pathname);
      navigate(["super_admin", "staff_admin"].includes(data.role) && location.pathname.startsWith("/admin") ? "/admin" : "/account", { replace: true });
    }).catch(() => {
      toast.error("Google sign-in failed");
      navigate("/login", { replace: true });
    });
  }, [location, navigate, setUser]);
  return <div className="min-h-screen grid place-items-center text-sm text-stone-500">Signing you in…</div>;
}

export default function Auth({ mode = "login", admin = false }) {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", mobile: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const titles = { login: admin ? "Admin sign in" : "Welcome back", register: "Create account", forgot: "Reset password", reset: "Set new password" };
  useSEO({ title: titles[mode] });
  const next = sp.get("next") || (admin ? "/admin" : "/account");

  useEffect(() => {
    if (user && mode === "login" && (!admin || ["super_admin", "staff_admin"].includes(user.role))) navigate(next, { replace: true });
  }, [user, mode, admin, next, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      if (mode === "login") {
        const u = await login(f.email, f.password);
        if (admin && !["super_admin", "staff_admin"].includes(u.role)) throw new Error("This account does not have admin access");
        navigate(next);
      } else if (mode === "register") {
        await register({ name: f.name, email: f.email, password: f.password, mobile: f.mobile || null });
        navigate("/account");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: f.email });
        setMsg(data.message + " (Email sending is not active yet — the link is logged for the admin in Notifications.)");
      } else {
        await api.post("/auth/reset-password", { token: sp.get("token"), password: f.password });
        toast.success("Password updated. Please log in.");
        navigate("/login");
      }
    } catch (er) {
      setMsg(er.response ? errMsg(er) : er.message);
    }
    setBusy(false);
  };

  return (
    <div className={`${admin ? "min-h-screen bg-[#F4EFE6]" : ""} grid place-items-center px-4 py-16`}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#FFFDF9] border border-[#E8E0D5] rounded-2xl p-6 sm:p-8 space-y-4" data-testid={`auth-form-${mode}`}>
        <p className="eyebrow">SEED COSMETICS{admin ? " · Admin" : ""}</p>
        <h1 className="text-4xl text-[#1B3B2B]">{titles[mode]}</h1>
        {mode === "register" && <input required placeholder="Full name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="auth-name-input" />}
        {mode !== "reset" && <input required type="email" placeholder="Email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="auth-email-input" />}
        {mode === "register" && <input placeholder="Mobile (optional)" inputMode="numeric" className={inp} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="auth-mobile-input" />}
        {mode !== "forgot" && <input required type="password" minLength={mode === "login" ? 1 : 8} placeholder={mode === "login" ? "Password" : "Password (min 8 characters)"} className={inp} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="auth-password-input" />}
        {msg && <p className="text-sm text-[#B91C1C] bg-red-50 rounded-lg p-3" data-testid="auth-message">{msg}</p>}
        <button disabled={busy} className="w-full bg-[#1B3B2B] text-[#FBF8F3] rounded-full py-3.5 text-sm hover:bg-[#2C523A] disabled:opacity-50" data-testid="auth-submit-button">{busy ? "Please wait…" : titles[mode]}</button>
        {(mode === "login" || mode === "register") && (
          <button type="button" onClick={() => googleLogin(admin ? "/admin" : "/account")} className="w-full border border-[#E8E0D5] rounded-full py-3 text-sm hover:bg-[#F4EFE6]" data-testid="auth-google-button">Continue with Google</button>
        )}
        <div className="flex justify-between text-sm text-stone-600">
          {mode === "login" && <><Link to="/forgot-password" className="underline" data-testid="auth-forgot-link">Forgot password?</Link>{!admin && <Link to="/register" className="underline" data-testid="auth-register-link">Create account</Link>}</>}
          {mode === "register" && <Link to="/login" className="underline">Already have an account? Log in</Link>}
          {(mode === "forgot" || mode === "reset") && <Link to="/login" className="underline">Back to login</Link>}
        </div>
      </form>
    </div>
  );
}
